import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Send, Save, Archive, Copy, Loader2, MessageSquare, Circle, Lock } from 'lucide-react';
import { api, inputCls, Button, Section, Chips, Notice, timeAgo } from './ui';

/**
 * Saved deals. There is no terms form: you explain the deal to the Deal desk
 * agent, it asks until nothing is missing, and the card fills in as you talk.
 * Once saved, the agents can offer it to affiliates.
 */

const DEAL_STATUS = {
  draft: { label: 'Draft', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  active: { label: 'Active', cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  archived: { label: 'Archived', cls: 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400' }
};

const STRUCTURE_LABEL = { revshare: 'RevShare', cpa: 'CPA', hybrid: 'Hybrid', fixed_fee: 'Fixed / listing fee' };

const FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'draft', label: 'Drafts' },
  { key: 'archived', label: 'Archived' },
  { key: 'all', label: 'All' }
];

export const EXAMPLES = [
  '25% RevShare for UK casino affiliates, can go to 35% for big sites',
  'CPA from 60 EUR up to 100 EUR for Canada, 20 EUR deposit and 100 EUR wagered to qualify',
  'Sportsbook deal for Brazil, 30% RevShare, no fixed fees'
];

// Changes people make to a deal, typed into the chat. Each fills the box to finish.
export const EDITS = [
  'Add the welcome offer: ',
  'No welcome offer',
  'Lowest CPA to start at: ',
  'RevShare 40% for months 1 to 3, then 35% from month 4',
  'To qualify for CPA a player must wager ',
  'Affiliates get a postback URL for ',
  'Raise the RevShare budget to ',
  'Add Ireland',
  'Rename it '
];

// What new players get, as the deal desk recorded it.
const welcomeText = (w) => {
  if (!w || (w.has == null && !w.text)) return null;
  if (w.text) return w.text;
  return w.has ? 'Yes, details not given yet' : 'None';
};

const has = (v) => v !== null && v !== undefined && v !== '';
const pct = (v) => (has(v) ? `${v}%` : null);
const money = (v, cur) => (has(v) ? `${v} ${cur}`.trim() : null);
const plural = (n, word) => `${n || 0} ${word}${n === 1 ? '' : 's'}`;
const both = (cpa, rs, cur) => [has(cpa) && `${money(cpa, cur)} CPA`, has(rs) && `${pct(rs)} RevShare`].filter(Boolean).join(' + ') || null;

// Free-form term values (tiers, levers) can be text, lists or small objects.
const asText = (v) => {
  if (!has(v)) return null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.map(asText).filter(Boolean).join('; ') || null;
  if (typeof v === 'object') {
    return Object.entries(v).filter(([, x]) => has(x)).map(([k, x]) => `${k.replace(/_/g, ' ')} ${asText(x)}`).join(', ') || null;
  }
  return String(v);
};

// Which affiliates' traffic a deal is for. PPC affiliates pay for clicks, so they usually get their own deal.
export const TRAFFIC_LABEL = { any: 'SEO and PPC', seo: 'SEO only (organic)', ppc: 'PPC only (paid ads)' };

// Where the agent opens, how it climbs and its budget for one structure, in the
// words the deal desk uses. The opening and the steps are worked out by the
// server (RevShare at 60% of the budget unless a lower first offer is set, CPA
// at its lowest; on a deal priced by position, the lowest position's).
const OPENS = 'Agent opens at';
const STEPS = 'Steps if pushed';
const BUDGET = 'Budget (most it may agree to)';
// What a player must wager before a CPA is paid, as the deal desk recorded it.
const wageringText = (w) => {
  if (!w || (w.has == null && !w.text)) return null;
  if (w.text) return w.text;
  return w.has ? 'Yes, details not given yet' : 'No, the deposit alone qualifies';
};
// A RevShare that steps down after the first months.
const laterText = (l) => {
  if (!l || l.has == null) return null;
  if (l.has === false) return 'Same rate every month';
  return has(l.pct) && has(l.after_months) ? `${l.pct}% at most from month ${l.after_months + 1}` : 'Yes, details not given yet';
};
const limitsFor = (key, t, cur, steps) => {
  const x = t[key] || {};
  const s = steps?.[key] || {};
  if (key === 'revshare') return [[OPENS, s.opens], [STEPS, s.steps], [BUDGET, pct(x.max)], ['After the first months', laterText(t.revshare_later)]];
  if (key === 'cpa') return [['Lowest CPA (agent starts)', money(x.open, cur) || s.opens], [STEPS, s.steps], ['Most CPA (budget)', money(x.max, cur)], ['CPA baseline', money(x.baseline, cur)], ['Wagering to qualify', wageringText(t.cpa_wagering)]];
  if (key === 'hybrid') return [[OPENS, s.opens], [STEPS, s.steps], [BUDGET, both(x.cpa_max, x.rs_max, cur)], ['CPA baseline', money(x.baseline, cur)], ['Wagering to qualify', wageringText(t.cpa_wagering)]];
  if (key === 'fixed_fee') return [['Monthly limit', money(x.max_monthly, cur)]];
  return [];
};

const StatusPill = ({ status }) => {
  const s = DEAL_STATUS[status] || DEAL_STATUS.draft;
  return <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span>;
};

const Row = ({ label, children }) => (
  <div className="flex gap-3 py-0.5 text-sm">
    <span className="w-28 flex-none text-gray-500 dark:text-slate-400 sm:w-44">{label}</span>
    <span className={children ? 'text-gray-800 dark:text-slate-200' : 'text-gray-400 dark:text-slate-500'}>{children || 'Not set'}</span>
  </div>
);

const DealItem = ({ offer, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full rounded-xl border p-3 text-left transition-colors ${selected
      ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20'
      : 'border-gray-200 bg-white hover:border-gray-300 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700'}`}
  >
    <div className="flex items-start justify-between gap-2">
      <span className="font-medium text-gray-900 dark:text-slate-100">{offer.name || 'Untitled deal'}</span>
      <StatusPill status={offer.status} />
    </div>
    {offer.opening_line && <div className="mt-0.5 line-clamp-2 text-sm text-gray-600 dark:text-slate-400">{offer.opening_line}</div>}
    <div className="mt-1 text-xs text-gray-500 dark:text-slate-400">
      {(offer.geos || []).join(', ') || 'All markets'}{offer.currency ? ` · ${offer.currency}` : ''}{(offer.licences || []).length ? ` · ${offer.licences.join(', ')}` : ''}
      {offer.traffic && offer.traffic !== 'any' ? ` · ${TRAFFIC_LABEL[offer.traffic]}` : ''}
    </div>
    <div className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
      Used by {plural(offer.used_by_teams?.length, 'team')} · {plural(offer.opportunities, 'affiliate')} · {offer.won || 0} won
      {offer.updated_at ? ` · updated ${timeAgo(offer.updated_at)}` : ''}
    </div>
  </button>
);

const DealCard = ({ offer, marketName }) => {
  const t = offer.draft_terms ?? offer.terms ?? {};
  const cur = t.currency || offer.currency || '';
  const structures = t.allowed || t.structures || offer.structures || [];
  const geos = t.geos || offer.geos || [];
  const licences = t.licences?.length ? t.licences : offer.licences || [];
  const traffic = t.traffic || offer.traffic;
  const lead = t.lead_with_offer ?? offer.lead_with_offer;
  return (
    <div>
      <Row label="Structures">{structures.map((s) => STRUCTURE_LABEL[s] || s).join(', ')}</Row>
      <Row label="Currency">{cur}</Row>
      <Row label="Brand licences">{licences.join(', ')}</Row>
      {structures.map((s) => (
        <div key={s} className="my-2 rounded-lg border border-gray-100 px-3 py-2 dark:border-slate-800">
          <div className="mb-1 text-xs font-medium text-gray-700 dark:text-slate-300">{STRUCTURE_LABEL[s] || s}</div>
          {limitsFor(s, t, cur, offer.steps).map(([label, value]) => <Row key={label} label={label}>{value}</Row>)}
        </div>
      ))}
      {(offer.position_steps || []).length > 0 && (
        <div className="my-2 rounded-lg border border-gray-100 px-3 py-2 dark:border-slate-800">
          <div className="mb-1 text-xs font-medium text-gray-700 dark:text-slate-300">By position (asked before any number; with none given, the lowest)</div>
          {offer.position_steps.map((p) => (
            <Row key={p.positions} label={`Positions ${p.positions}`}>
              {Object.entries(p).filter(([k]) => k !== 'positions').map(([k, d]) => `${STRUCTURE_LABEL[k] || k}: opens ${d.opens}, up to ${d.budget}`).join('; ')}
            </Row>
          ))}
        </div>
      )}
      <Row label="Tiers">{asText(t.tiers)}</Row>
      <Row label="Welcome offer">{welcomeText(t.welcome_offer)}</Row>
      <Row label="Postback URL (S2S)">{t.postback?.text || (t.postback?.has === true ? 'Yes, details not given yet' : t.postback?.has === false ? 'No' : null)}</Row>
      <Row label="Track record">{asText(t.track_record)}</Row>
      <Row label="Programme facts">{asText(t.programme_facts)}</Row>
      <Row label="Markets">{geos.length ? geos.map(marketName).join(', ') : 'All markets'}</Row>
      <Row label="Traffic">{traffic ? TRAFFIC_LABEL[traffic] : null}</Row>
      <Row label="Who it is for">{asText(t.target ?? offer.target)}</Row>
      <Row label="Negative carryover">{asText(t.negative_carryover)}</Row>
      <Row label="Payment terms">{asText(t.payment_terms)}</Row>
      <Row label="Things we can trade">{asText(t.other_levers)}</Row>
      <Row label="Notes">{asText(t.notes)}</Row>
      <Row label="Mention in the first email">{lead == null ? null : lead ? 'Yes' : 'No'}</Row>
    </div>
  );
};

export const Bubble = ({ role, content, at, waiting }) => {
  const mine = role === 'user';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-xl border px-3 py-2 text-sm ${mine
        ? 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/40'
        : 'border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900'} ${waiting ? 'opacity-70' : ''}`}>
        <div className="mb-0.5 text-xs text-gray-500 dark:text-slate-400">{mine ? 'You' : 'Deal desk'}{at ? ` · ${timeAgo(at)}` : ''}</div>
        <div className="whitespace-pre-wrap text-gray-800 dark:text-slate-200">{content}</div>
      </div>
    </div>
  );
};

export default function Deals({ meta, focusId, onSaved }) {
  const [offers, setOffers] = useState([]);
  const [filter, setFilter] = useState(['active', 'draft']);
  const [offer, setOffer] = useState(null); // open deal, null for a new one
  const [loadingId, setLoadingId] = useState(null);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(null); // message waiting for the deal desk
  const [name, setName] = useState(null); // null until the user renames
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  // The desk's answer to a first message it will not act on (a plan upgrade, a change to MARS): no deal is started.
  const [aside, setAside] = useState(null);
  const view = useRef(0); // bumps on every switch, so late answers are dropped
  const chatRef = useRef(null);
  const inputRef = useRef(null);

  const markets = meta?.markets || [];
  const marketName = (code) => markets.find((m) => m.code === code)?.name || code;
  const upsert = (o) => setOffers((list) => [o, ...list.filter((x) => x.id !== o.id)]);

  useEffect(() => {
    api('/offers?status=all').then((d) => setOffers(d.offers || [])).catch((err) => setError(err.message));
  }, []);

  // empty workspace; returns the token for the new view
  const reset = useCallback(() => {
    view.current += 1;
    setOffer(null);
    setLoadingId(null);
    setError(null);
    setAside(null);
    setName(null);
    setPending(null);
    setInput('');
    return view.current;
  }, []);

  const open = useCallback(async (id) => {
    const token = reset();
    setLoadingId(id);
    try {
      const d = await api(`/offers/${id}`);
      if (view.current === token) setOffer(d.offer);
    } catch (err) {
      if (view.current === token) setError(err.message);
    } finally {
      if (view.current === token) setLoadingId(null);
    }
  }, [reset]);

  useEffect(() => { if (focusId) open(focusId); }, [focusId, open]);

  const chat = offer?.chat || [];
  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.length, pending]);

  const newDeal = () => {
    reset();
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  const send = async () => {
    const message = input.trim();
    if (!message || pending) return;
    const token = view.current;
    setPending(message);
    setInput('');
    setError(null);
    setAside(null);
    try {
      const d = await api('/offers/chat', { method: 'POST', body: { offer_id: offer?.id, message } });
      if (!d.offer) {
        if (view.current === token) setAside({ message, reply: d.reply });
        return;
      }
      const history = Array.isArray(d.offer.chat) ? d.offer.chat
        : [...chat, { role: 'user', content: message }, { role: 'assistant', content: d.reply }];
      const next = { ...d.offer, chat: history, ready: d.ready ?? d.offer.ready, missing: d.missing ?? d.offer.missing };
      upsert(next);
      if (view.current === token) {
        setOffer(next);
        if (d.saved) setName(null);
      }
      // A yes in the chat saves the deal, the same as the button.
      if (d.saved && onSaved) onSaved(d.offer);
    } catch (err) {
      if (view.current === token) {
        setError(err.message);
        setInput(message);
      }
    } finally {
      if (view.current === token) setPending(null);
    }
  };

  // save, archive and duplicate all answer with the offer
  const act = async (key) => {
    const token = view.current;
    setBusy(key);
    setError(null);
    try {
      const body = key === 'save' ? { name: name?.trim() || undefined } : undefined;
      const d = await api(`/offers/${offer.id}/${key}`, { method: 'POST', body });
      upsert(d.offer);
      if (view.current !== token) return;
      if (key === 'duplicate') view.current += 1;
      setOffer(d.offer);
      setName(null);
      if (key === 'save' && onSaved) onSaved(d.offer);
    } catch (err) {
      if (view.current === token) setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const pickFilter = (next) => {
    const added = next.find((k) => !filter.includes(k));
    const clean = added === 'all' ? ['all'] : next.filter((k) => k !== 'all');
    setFilter(clean.length ? clean : ['all']);
  };
  const count = (key) => (key === 'all' ? offers.length : offers.filter((o) => o.status === key).length);
  const shown = offers.filter((o) => filter.includes('all') || filter.includes(o.status));

  const selectedId = loadingId ?? offer?.id ?? null;
  const missing = offer?.missing || [];
  const unsaved = !!offer && (offer.status === 'draft' || offer.draft_terms != null);

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <aside className="space-y-3 lg:w-80 lg:flex-none">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-gray-600 dark:text-slate-400">Deals the agents can offer.</p>
          <Button variant="primary" icon={Plus} onClick={newDeal}>New deal</Button>
        </div>
        <Chips options={FILTERS} value={filter} onChange={pickFilter} render={(o) => `${o.label} ${count(o.key)}`} />
        <div className="space-y-2 lg:max-h-[70vh] lg:overflow-y-auto">
          {!shown.length && (
            <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-slate-800 dark:text-slate-400">No deals here yet.</div>
          )}
          {shown.map((o) => <DealItem key={o.id} offer={o} selected={o.id === selectedId} onClick={() => open(o.id)} />)}
        </div>
      </aside>

      <div className="min-w-0 flex-1 space-y-4">
        {error && <Notice tone="error">{error}</Notice>}

        {loadingId != null ? (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">Loading...</div>
        ) : offer ? (
          <Section
            title="Deal card"
            hint={offer.summary || 'Fills in as you talk to the deal desk.'}
            actions={(
              <div className="flex items-center gap-2">
                {offer.status === 'active' && offer.draft_terms != null && <span className="text-xs text-amber-700 dark:text-amber-300">Changes not saved</span>}
                <StatusPill status={offer.status} />
              </div>
            )}
          >
            <DealCard offer={offer} marketName={marketName} />

            {missing.length > 0 && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                <div className="font-medium">Still needed:</div>
                <ul className="mt-1 space-y-0.5">
                  {missing.map((m) => <li key={m} className="flex items-center gap-1.5"><Circle className="h-3 w-3 flex-none" />{m}</li>)}
                </ul>
              </div>
            )}
            {(offer.worth_updating || []).length > 0 && (
              <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
                <div className="font-medium">Worth updating (the agents work without it):</div>
                <ul className="mt-1 space-y-0.5">
                  {offer.worth_updating.map((m) => <li key={m} className="flex items-start gap-1.5"><Circle className="mt-1 h-3 w-3 flex-none" />{m}</li>)}
                </ul>
              </div>
            )}
            {offer.ready && unsaved && (
              <div className="mt-4"><Notice tone="ok">The deal desk has everything it needs. Check the card, then say yes in the chat or save it here.</Notice></div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <input className={`${inputCls} sm:w-64`} aria-label="Deal name" placeholder="Deal name" value={name ?? offer.name ?? ''} onChange={(e) => setName(e.target.value)} />
              <Button variant="primary" icon={Save} busy={busy === 'save'} disabled={!offer.ready || !!pending || !!busy} onClick={() => act('save')}>Save deal</Button>
              {offer.status === 'active' && <Button icon={Archive} busy={busy === 'archive'} disabled={!!busy} onClick={() => act('archive')}>Archive</Button>}
              <Button icon={Copy} busy={busy === 'duplicate'} disabled={!!busy} onClick={() => act('duplicate')}>Duplicate</Button>
            </div>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400">
              <Lock className="h-3.5 w-3.5 flex-none" />The budget stays internal: affiliates never see it, and anything above it waits for you to approve.
            </p>
          </Section>
        ) : !pending && (
          <div className="rounded-xl border border-dashed border-gray-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
            <MessageSquare className="mb-2 h-6 w-6 text-blue-600 dark:text-blue-400" />
            <h3 className="font-semibold text-gray-900 dark:text-slate-100">Tell the deal desk about the deal</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">
              Explain it the way you would to a colleague. The deal desk asks about anything missing, like limits, markets or payment terms, and fills in the deal card as you go.
            </p>
            <div className="mt-4 space-y-2">
              {EXAMPLES.map((text) => (
                <button key={text} type="button" onClick={() => { setInput(text); if (inputRef.current) inputRef.current.focus(); }}
                  className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left text-sm text-gray-700 hover:border-blue-300 hover:bg-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-blue-800 dark:hover:bg-blue-950/40">
                  {text}
                </button>
              ))}
            </div>
          </div>
        )}

        {loadingId == null && (
          <div className="rounded-xl border border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            {(chat.length > 0 || pending || aside) && (
              <div ref={chatRef} className="max-h-[28rem] space-y-2 overflow-y-auto rounded-t-xl bg-gray-50 p-4 dark:bg-slate-950">
                {chat.map((m, i) => <Bubble key={`${i}-${m.at || ''}`} role={m.role} content={m.content} at={m.at} />)}
                {aside && !pending && <Bubble role="user" content={aside.message} />}
                {aside && !pending && <Bubble role="assistant" content={aside.reply} />}
                {pending && <Bubble role="user" content={pending} waiting />}
                {pending && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />Deal desk is thinking...
                  </div>
                )}
              </div>
            )}
            {offer && offer.status !== 'archived' && !pending && (
              <div className="flex flex-wrap items-center gap-1.5 border-t border-gray-100 px-3 pt-3 dark:border-slate-800">
                <span className="text-xs text-gray-500 dark:text-slate-400">Change it by chat:</span>
                {EDITS.map((text) => (
                  <button key={text} type="button"
                    onClick={() => { setInput(text); if (inputRef.current) inputRef.current.focus(); }}
                    className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-700 hover:border-blue-300 hover:bg-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-blue-800 dark:hover:bg-blue-950/40">
                    {text.trim()}
                  </button>
                ))}
              </div>
            )}
            <div className={`flex items-end gap-2 p-3 ${offer && offer.status !== 'archived' && !pending ? '' : 'border-t border-gray-100 dark:border-slate-800'}`}>
              <textarea
                ref={inputRef}
                rows={2}
                className={`${inputCls} resize-none`}
                placeholder={offer?.status === 'archived'
                  ? 'This deal is archived. Duplicate it to change it.'
                  : offer ? 'Tell the deal desk what to change, e.g. "add the welcome offer". Say yes to its summary and it is saved.' : 'Describe the deal, e.g. who it is for, the structure and how far you can go'}
                value={input}
                disabled={!!pending || offer?.status === 'archived'}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <Button variant="primary" icon={Send} busy={!!pending} disabled={!input.trim()} onClick={() => send()}>Send</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
